import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Loader2, Check, ZoomIn, X } from "lucide-react";
import { toast } from '@/components/ui/use-toast';

interface UnsplashImage {
  id: string;
  urls: {
    small: string;
    regular: string;
    full: string;
  };
  alt_description: string;
  user: {
    name: string;
  };
  links: {
    html: string;
  };
}

interface ImageSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectImage: (imageUrl: string) => void;
  initialQuery?: string;
}

import { supabase } from '@/integrations/supabase/client';

const ImageSearchModal: React.FC<ImageSearchModalProps> = ({
  isOpen,
  onClose,
  onSelectImage,
  initialQuery = ''
}) => {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [images, setImages] = useState<UnsplashImage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [zoomedImage, setZoomedImage] = useState<UnsplashImage | null>(null);

  const searchImages = useCallback(async (query: string, pageNum: number = 1, append: boolean = false) => {
    if (!query.trim()) return;

    setIsLoading(true);
    try {
      // Use our secure edge function instead of direct API calls
      const { data, error } = await supabase.functions.invoke('search-images', {
        body: {
          query,
          page: pageNum,
          per_page: 20
        }
      });

      if (error || data.error) {
        console.error('Error calling search-images function:', error || data.error);
        // Fallback to demo images
        const demoImages = generateDemoImages(query);
        setImages(append ? prev => [...prev, ...demoImages] : demoImages);
        setHasMore(false);
        
        toast({
          title: "Using demo images",
          description: data.fallback ? "Unsplash API unavailable. Showing sample images." : "Could not connect to image service. Showing sample images.",
          variant: "default",
        });
        return;
      }

      setImages(append ? prev => [...prev, ...data.results] : data.results);
      setHasMore(data.total_pages > pageNum);
    } catch (error) {
      console.error('Error searching images:', error);
      // Fallback to demo images
      const demoImages = generateDemoImages(query);
      setImages(append ? prev => [...prev, ...demoImages] : demoImages);
      setHasMore(false);
      
      toast({
        title: "Using demo images",
        description: "Could not connect to image service. Showing sample images.",
        variant: "default",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Auto-search when modal opens with initial query
  React.useEffect(() => {
    if (isOpen && initialQuery.trim()) {
      setSearchQuery(initialQuery);
      searchImages(initialQuery, 1, false);
    }
  }, [isOpen, initialQuery, searchImages]);

  const generateDemoImages = (query: string): UnsplashImage[] => {
    const imageQueries = [
      'professional-business-workspace',
      'modern-office-setup',
      'business-tools-equipment',
      'clean-workspace-desk',
      'professional-environment',
      'business-meeting-room'
    ];

    return imageQueries.map((imgQuery, index) => ({
      id: `demo-${index}`,
      urls: {
        small: `https://images.unsplash.com/photo-150${7 + index}00321${1690 + index * 100}-0a1dd7228f2d?w=400&h=200&fit=crop&auto=format`,
        regular: `https://images.unsplash.com/photo-150${7 + index}00321${1690 + index * 100}-0a1dd7228f2d?w=800&h=400&fit=crop&auto=format`,
        full: `https://images.unsplash.com/photo-150${7 + index}00321${1690 + index * 100}-0a1dd7228f2d?w=1200&h=600&fit=crop&auto=format`
      },
      alt_description: `Professional ${query} workspace ${index + 1}`,
      user: {
        name: 'Unsplash Demo'
      },
      links: {
        html: '#'
      }
    }));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSelectedImageId(null);
    searchImages(searchQuery, 1, false);
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    searchImages(searchQuery, nextPage, true);
  };

  const handleSelectImage = (image: UnsplashImage) => {
    setSelectedImageId(image.id);
  };

  const handleConfirmSelection = () => {
    const selectedImage = images.find(img => img.id === selectedImageId);
    if (selectedImage) {
      onSelectImage(selectedImage.urls.regular);
      onClose();
    }
  };

  const handleClose = () => {
    setImages([]);
    setSelectedImageId(null);
    setPage(1);
    setHasMore(true);
    setZoomedImage(null);
    onClose();
  };

  const handleImageZoom = (image: UnsplashImage, e: React.MouseEvent) => {
    e.stopPropagation();
    setZoomedImage(image);
  };

  const handleCloseZoom = () => {
    setZoomedImage(null);
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Search Banner Images</DialogTitle>
          <DialogDescription>
            Search for professional banner images for your category. All images are landscape-oriented and business-appropriate.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Search Form */}
          <form onSubmit={handleSearch} className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search for banner images (e.g., 'professional office', 'construction tools')"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button type="submit" disabled={isLoading || !searchQuery.trim()}>
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
            </Button>
          </form>

          {/* Image Grid */}
          <div className="flex-1 overflow-y-auto">
            {images.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {images.map((image) => (
                  <div
                    key={image.id}
                    className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all group ${
                      selectedImageId === image.id
                        ? 'border-primary ring-2 ring-primary/20'
                        : 'border-transparent hover:border-gray-300'
                    }`}
                    onClick={() => handleSelectImage(image)}
                  >
                    <img
                      src={image.urls.small}
                      alt={image.alt_description || 'Unsplash image'}
                      className="w-full h-24 object-cover"
                      loading="lazy"
                    />
                    
                    {/* Zoom button */}
                    <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="h-6 w-6 p-0 bg-black/50 hover:bg-black/70 border-none"
                        onClick={(e) => handleImageZoom(image, e)}
                      >
                        <ZoomIn className="h-3 w-3 text-white" />
                      </Button>
                    </div>
                    
                    {/* Selection indicator */}
                    {selectedImageId === image.id && (
                      <div className="absolute top-2 right-2 bg-primary text-white rounded-full p-1">
                        <Check className="h-3 w-3" />
                      </div>
                    )}
                    
                    {/* Image attribution */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                      <p className="text-white text-xs truncate">
                        by {image.user.name}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <Search className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-lg font-medium">
                  {isLoading ? "Searching..." : "Search for banner images"}
                </p>
                <p className="text-sm">
                  {isLoading ? "Loading professional images..." : "Enter keywords like \"business\", \"office\", or your category name"}
                </p>
              </div>
            )}

            {/* Load More Button */}
            {images.length > 0 && hasMore && (
              <div className="flex justify-center mt-4 pb-4">
                <Button
                  variant="outline"
                  onClick={loadMore}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirmSelection}
            disabled={!selectedImageId}
          >
            Use Selected Image
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Zoom Modal */}
    {zoomedImage && (
      <Dialog open={!!zoomedImage} onOpenChange={handleCloseZoom}>
        <DialogContent className="sm:max-w-[95vw] max-h-[95vh] p-0 overflow-hidden">
          <div className="relative w-full h-full">
            {/* Close button */}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 border-none"
              onClick={handleCloseZoom}
            >
              <X className="h-4 w-4 text-white" />
            </Button>
            
            {/* Large image */}
            <img
              src={zoomedImage.urls.regular}
              alt={zoomedImage.alt_description || 'Zoomed Unsplash image'}
              className="w-full h-auto max-h-[90vh] object-contain"
            />
            
            {/* Image info overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
              <div className="flex justify-between items-end text-white">
                <div>
                  <p className="text-sm opacity-90">Photo by</p>
                  <p className="text-lg font-medium">{zoomedImage.user.name}</p>
                  {zoomedImage.alt_description && (
                    <p className="text-sm opacity-75 mt-1">{zoomedImage.alt_description}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setSelectedImageId(zoomedImage.id);
                      handleCloseZoom();
                    }}
                    className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                  >
                    Select This Image
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
};

export default ImageSearchModal;